import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { fetchEventSource } from "@microsoft/fetch-event-source";
import { tokenManager } from "../utils/tokenManager";
import { userTimezoneHeaders } from "../utils/userTimezoneHeader";
import { buildAlertsStreamSearchParams } from "../utils/alertStreamUtils";

const API_BASE_URL = process.env.REACT_APP_API_URL || "";

const ALERT_EVENTS = new Set([
  "alert_created",
  "alert_status_updated",
  "alert_lifecycle_updated",
]);

/**
 * Subscribe to GET {API}/alerts/stream with optional filters (reconnects when filters change).
 * Uses fetch-based SSE so Authorization and X-User-Timezone headers are supported.
 *
 * @param {Record<string, unknown>} filters — passed to {@link buildAlertsStreamSearchParams}
 * @param {{ enabled?: boolean }} [options]
 * @returns {{
 *   alertsById: Record<string, Record<string, unknown>>,
 *   connectionState: 'idle' | 'connecting' | 'open' | 'closed' | 'error',
 *   lastError: Error | null,
 *   clearAlerts: () => void,
 * }}
 */
export function useAlertStream(filters, options = {}) {
  const { enabled = true } = options;
  const [alertsById, setAlertsById] = useState({});
  const [connectionState, setConnectionState] = useState("idle");
  const [lastError, setLastError] = useState(null);
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  const filterSignature = useMemo(() => {
    const p = buildAlertsStreamSearchParams(filters || {});
    const entries = Array.from(p.entries()).sort(([a], [b]) =>
      a.localeCompare(b),
    );
    return JSON.stringify(entries);
  }, [filters]);

  const clearAlerts = useCallback(() => {
    setAlertsById({});
  }, []);

  useEffect(() => {
    if (!enabled || !API_BASE_URL) {
      setConnectionState("idle");
      return undefined;
    }

    const ctrl = new AbortController();
    setAlertsById({});
    setLastError(null);
    setConnectionState("connecting");

    const params = buildAlertsStreamSearchParams(filtersRef.current || {});
    const qs = params.toString();
    const url = `${API_BASE_URL.replace(/\/$/, "")}/alerts/stream${
      qs ? `?${qs}` : ""
    }`;

    const token = tokenManager.getToken();

    const run = async () => {
      try {
        await fetchEventSource(url, {
          signal: ctrl.signal,
          headers: {
            Accept: "text/event-stream",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            ...userTimezoneHeaders(),
          },
          openWhenHidden: true,
          onopen(response) {
            const ct = response.headers.get("content-type") || "";
            if (
              response.ok &&
              (ct.includes("text/event-stream") || ct.includes("text/plain"))
            ) {
              setConnectionState("open");
              setLastError(null);
              return;
            }
            if (response.status === 401 || response.status === 403) {
              const err = new Error(
                `Alerts stream unauthorized (${response.status})`,
              );
              setLastError(err);
              setConnectionState("error");
              ctrl.abort();
              throw err;
            }
            if (response.status >= 400 && response.status < 500) {
              const err = new Error(`Alerts stream HTTP ${response.status}`);
              setLastError(err);
              setConnectionState("error");
              ctrl.abort();
              throw err;
            }
          },
          onmessage(ev) {
            if (ev.event === "ping") return;
            if (!ALERT_EVENTS.has(ev.event)) return;
            const raw = ev.data;
            if (raw == null || !String(raw).trim()) return;
            try {
              const payload = JSON.parse(String(raw));
              const sid =
                payload?.id != null
                  ? String(payload.id)
                  : payload?._id != null
                    ? String(payload._id)
                    : null;
              if (!sid) return;
              setAlertsById((prev) => ({ ...prev, [sid]: payload }));
            } catch (e) {
              console.warn("[useAlertStream] JSON parse failed:", e);
            }
          },
          onerror(err) {
            if (ctrl.signal.aborted) return;
            console.error("[useAlertStream] connection error:", err);
            setLastError(
              err instanceof Error ? err : new Error(String(err ?? "SSE error")),
            );
            setConnectionState("error");
            throw err;
          },
        });
      } catch (e) {
        if (e?.name === "AbortError" || ctrl.signal.aborted) {
          setConnectionState("closed");
          return;
        }
        console.error("[useAlertStream] failed:", e);
        setLastError(e instanceof Error ? e : new Error(String(e)));
        setConnectionState("error");
      }
    };

    void run();

    return () => {
      ctrl.abort();
    };
  }, [enabled, filterSignature]);

  return {
    alertsById,
    connectionState,
    lastError,
    clearAlerts,
  };
}
